/**
 * Shared job row for the calendar day panel and week sign-off.
 * Renders one booking / logged day: facts, invoice narrative, actions.
 */
import {
  joinSiteNames,
  renderInvoiceNarrative,
} from '../../../src/invoicing/narrative.js';
import { formatPenceGBP } from '../../../src/invoicing/money.js';
import {
  RateResolutionError,
  resolveRate,
  type DurationType,
} from '../../../src/domain/rates.js';
import {
  isLineKind,
  showsDuration,
  usesDirectAmount,
  type LineKind,
} from '../domain/lineKind.js';
import type {
  CachedClient,
  CachedRateOverride,
  CachedSite,
  CachedWorkType,
  LocalJob,
} from '../db/jobsDb.js';

export type JobRowAction = 'confirm' | 'edit' | 'delete' | 'unrelease';

export type JobRowView = {
  id: string;
  clientName: string;
  siteLabel: string;
  workTypeLabel: string;
  durationLabel: string;
  rateLabel: string | null;
  narrative: string;
  statusKind: 'planned' | 'completed' | 'cancelled';
  statusLabel: string;
  invoiced: boolean;
  invoiceLabel: string | null;
  dateLabel?: string | null;
  extraPills?: string[];
  actions: JobRowAction[];
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function kindOf(lineKind: string | undefined): LineKind {
  return lineKind && isLineKind(lineKind) ? lineKind : 'day_rate';
}

export function durationLabelForJob(
  job: {
    duration_type: DurationType | string;
    custom_hours?: number | string | null;
    custom_amount?: number | string | null;
  },
  lineKind?: string,
): string {
  const kind = kindOf(lineKind);
  if (kind === 'expense' || usesDirectAmount(kind)) {
    return kind === 'expense' ? 'Expense' : 'Fixed fee';
  }
  if (job.duration_type === 'full_day') return 'Full day';
  if (job.duration_type === 'half_day') return 'Half day';
  if (job.custom_hours != null) return `${job.custom_hours} hours`;
  if (job.custom_amount != null)
    return `£${Number(job.custom_amount).toFixed(2)}`;
  return 'Custom';
}

export function resolveJobRateLabel(
  job: LocalJob,
  client: CachedClient | undefined,
  workType: CachedWorkType | undefined,
  override: CachedRateOverride | null,
): string {
  const kind = kindOf(workType?.line_kind);
  if (showsDuration(kind) && job.duration_type !== 'custom') {
    try {
      const { amountPence } = resolveRate(
        {
          duration_type: job.duration_type,
          custom_hours: null,
          custom_amount: null,
        },
        {
          day_rate: client?.day_rate ?? '',
          half_day_rate: client?.half_day_rate ?? '',
          hourly_rate: client?.hourly_rate ?? null,
        },
        override
          ? {
              day_rate: override.day_rate,
              half_day_rate: override.half_day_rate,
              hourly_rate: override.hourly_rate,
            }
          : null,
      );
      return amountPence > 0 ? formatPenceGBP(amountPence) : 'No rate configured';
    } catch (err) {
      return err instanceof RateResolutionError
        ? err.message
        : 'No rate configured';
    }
  }
  if (job.custom_amount != null) {
    return `£${Number(job.custom_amount).toFixed(2)}`;
  }
  if (job.custom_hours != null) {
    return `${job.custom_hours}h (hourly)`;
  }
  return '—';
}

export function jobRowFromLocal(
  job: LocalJob,
  ctx: {
    clients: CachedClient[];
    workTypes: CachedWorkType[];
    sites: CachedSite[];
    overrides: CachedRateOverride[];
  },
): JobRowView {
  const client = ctx.clients.find((c) => c.id === job.client_id);
  const workType = ctx.workTypes.find((t) => t.id === job.work_type_id);
  const override =
    ctx.overrides.find(
      (o) =>
        o.client_id === job.client_id && o.work_type_id === job.work_type_id,
    ) ?? null;
  const siteNames = (job.site_ids ?? [])
    .map((id) => ctx.sites.find((s) => s.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  const siteLabel = joinSiteNames(siteNames, job.site_reference) || 'No site';
  const invoiced = Boolean(job.invoice_run_id);
  const completed = job.status === 'completed';
  const actions: JobRowAction[] = [];
  if (!invoiced && job.status === 'planned') actions.push('confirm');
  if (!invoiced && job.status !== 'cancelled') {
    actions.push('edit');
    actions.push('delete');
  }
  return {
    id: job.id,
    clientName: client?.name ?? 'Client',
    siteLabel,
    workTypeLabel: workType?.short_description ?? 'Work type',
    durationLabel: durationLabelForJob(job, workType?.line_kind),
    rateLabel: completed
      ? resolveJobRateLabel(job, client, workType, override)
      : null,
    narrative: renderInvoiceNarrative({
      template:
        workType?.invoice_narrative?.trim() ||
        '{date} – {site}',
      workDate: job.work_date.slice(0, 10),
      siteLabel: siteLabel === 'No site' ? '' : siteLabel,
      durationType: job.duration_type,
      halfDaySuffix: workType?.half_day_suffix ?? '',
      notes: job.notes,
    }),
    statusKind: job.status,
    statusLabel:
      job.status === 'planned'
        ? 'Provisional'
        : job.status === 'cancelled'
          ? 'Cancelled'
          : 'Confirmed',
    invoiced,
    invoiceLabel: job.invoice_number ? `#${job.invoice_number}` : invoiced ? 'invoice' : null,
    actions,
  };
}

export function jobRowHtml(row: JobRowView): string {
  const pills = [
    row.statusLabel,
    row.invoiced
      ? `Invoiced${row.invoiceLabel ? ` ${row.invoiceLabel}` : ''}`
      : null,
    ...(row.extraPills ?? []),
  ].filter((p): p is string => Boolean(p));
  const meta = [
    row.workTypeLabel,
    row.siteLabel,
    row.durationLabel,
    row.rateLabel,
  ]
    .filter((p): p is string => Boolean(p && p !== '—'))
    .join(' · ');
  const actionBtns = row.actions
    .map((a) => {
      if (a === 'confirm')
        return `<button type="button" class="btn-secondary btn-compact" data-job-confirm="${row.id}">Confirm</button>`;
      if (a === 'edit')
        return `<button type="button" class="btn-secondary btn-compact" data-job-edit="${row.id}">Edit</button>`;
      if (a === 'unrelease')
        return `<button type="button" class="btn-secondary btn-compact" data-job-unrelease="${row.id}">Un-release</button>`;
      return `<button type="button" class="btn-secondary btn-compact" data-job-delete="${row.id}">Delete</button>`;
    })
    .join('');
  const released = row.statusLabel === 'Released';
  const canEdit = row.actions.includes('edit');
  return `<article class="job-row st-${row.statusKind}${row.invoiced ? ' is-invoiced' : ''}${released ? ' is-released' : ''}" data-job-row="${row.id}"${canEdit ? ' data-job-can-edit="1"' : ''}>
    <div class="job-row-top">
      <strong>${escapeHtml(row.clientName)}</strong>
      <span class="job-row-pills">${pills
        .map((p) => `<span class="pill">${escapeHtml(p)}</span>`)
        .join('')}</span>
    </div>
    ${row.dateLabel ? `<p class="job-row-date">${escapeHtml(row.dateLabel)}</p>` : ''}
    <p class="job-row-meta">${escapeHtml(meta)}</p>
    <p class="job-row-narrative">${escapeHtml(row.narrative || '—')}</p>
    ${
      row.invoiced
        ? `<p class="job-row-lock">Invoiced${row.invoiceLabel ? ` ${escapeHtml(row.invoiceLabel)}` : ''} — read only</p>`
        : released
          ? `<p class="job-row-lock">Released — un-release to edit</p>`
          : ''
    }
    ${actionBtns ? `<div class="job-row-actions">${actionBtns}</div>` : ''}
  </article>`;
}

export type JobRowHandlers = {
  onConfirm?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onUnrelease?: (id: string) => void;
};

export function bindJobRowActions(
  root: ParentNode,
  handlers: JobRowHandlers,
): void {
  root.querySelectorAll('[data-job-confirm]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      handlers.onConfirm?.((el as HTMLElement).dataset.jobConfirm!);
    });
  });
  root.querySelectorAll('[data-job-edit]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      handlers.onEdit?.((el as HTMLElement).dataset.jobEdit!);
    });
  });
  root.querySelectorAll('[data-job-delete]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      handlers.onDelete?.((el as HTMLElement).dataset.jobDelete!);
    });
  });
  root.querySelectorAll('[data-job-unrelease]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      ev.stopPropagation();
      handlers.onUnrelease?.((el as HTMLElement).dataset.jobUnrelease!);
    });
  });
  root.querySelectorAll('[data-job-can-edit]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      if ((ev.target as HTMLElement).closest('button')) return;
      handlers.onEdit?.((el as HTMLElement).dataset.jobRow!);
    });
  });
}
